import type { POKanbanFilters } from "../../types/kanban.types";

/**
 * Recursively produces a deterministic JSON string from a value by sorting
 * object keys at every nesting level and sorting arrays of primitives.
 */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined) {
    return "null";
  }
  if (Array.isArray(value)) {
    const items = value.map(stableStringify);
    // Sort arrays of primitives for order-independent cache hits
    if (value.every((v) => typeof v === "string" || typeof v === "number")) {
      items.sort();
    }
    return `[${items.join(",")}]`;
  }
  if (typeof value === "object") {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort()
      .filter((k) => (value as Record<string, unknown>)[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value);
}

/**
 * Produces a deterministic string key from a filters object so that
 * equivalent filter sets always resolve to the same React Query cache entry.
 * Handles nested objects (dateRange, amountRange) and sorts at every level.
 */
export function canonicalizeFilters(filters: POKanbanFilters): string {
  return stableStringify(filters);
}

export const poKanbanKeys = {
  all: ["purchase-orders-kanban"] as const,

  purchaseOrders: {
    all: () => [...poKanbanKeys.all, "purchaseOrders"] as const,
    filtered: (filters: POKanbanFilters) =>
      [...poKanbanKeys.purchaseOrders.all(), canonicalizeFilters(filters)] as const,
    byId: (id: string) => [...poKanbanKeys.purchaseOrders.all(), id] as const,
  },

  statistics: {
    all: () => [...poKanbanKeys.all, "statistics"] as const,
  },

  vendors: {
    all: () => [...poKanbanKeys.all, "vendors"] as const,
    byIds: (ids: string[]) => [...poKanbanKeys.vendors.all(), [...ids].sort()] as const,
  },

  transfers: {
    all: () => [...poKanbanKeys.all, "transfers"] as const,
    active: () => [...poKanbanKeys.transfers.all(), "active"] as const,
  },

  paymentSummary: {
    all: () => [...poKanbanKeys.all, "paymentSummary"] as const,
    filtered: (vendorIds: string[]) =>
      [...poKanbanKeys.paymentSummary.all(), [...vendorIds].sort()] as const,
  },
};
